import React, {useCallback, useContext, useEffect} from 'react';

import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import {FlatButton} from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Icon, {Sizes as IconSizes, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {SlideBoxButton} from '@pb/kit-component-slide-box';


import {ConnectionStatus} from '../../components/connection-status';
import {CrmRow} from '../../components/crm-row';
import {useCrmLimits} from '../../useCrmLimits';
import {useEnabledCrmModules} from '../../useEnabledCrmModules';
import {useLinkageStatistic} from '../../useLinkageStatistic';
import {useSyncLinkageStatistic} from '../../useSyncLinkageStatistic';

import {MSDynamicsSection} from './ms-dynamics-section';
import {useGetMsDynamicsAuth} from './useGetMsDynamicsAuth';
import {useMSDynamicsCredentials} from './useMSDynamicsCredentials';
import {useMsDynamicsSettings} from './useMsDynamicsSettings';
import {isMsDynamicTestReady} from './utils/isMsDynamicTestReady';


import type {CrmComponentProps} from '../../license-crm-tab';

export const MSDynamicsRow = (props: CrmComponentProps) => {
    const {
        accountId,
        licenseId,
        currentCrm,
        runSaveChanges,
        runDiscardChanges,

        onChangeActiveCrm,
        onChangesSaved,
        onChangesDiscarded,
        onActionDisabled,
        editDisabled,
    } = props;

    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const {isSomeChunkChanged, isCurrentChunkChanged, setIsChunkChanged, onShowModal, proceed} = useConfirmationChunk({
        chunkName: 'ms-dynamics-crm',
        onProceed: () => {
            onChangesSaved();
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {
        isLoading: isModuleLoading,
        loadingError: moduleLoadingError,
        enabled,
        saveCrmModuleState,
    } = useEnabledCrmModules({licenseId, crmType: CrmTypes.MDCRM});

    const {
        isLoading: isLimitsLoading,
        loadingError: limitsLoadingError,
        isChanged: isCrmLimitsChanged,

        data: crmLimits,
        setData: setCrmLimits,

        saveChanges: saveCrmLimits,
        discardChanges: discardCrmLimits,
    } = useCrmLimits({licenseId, crmType: CrmTypes.MDCRM, accountId});

    const {
        isLoading: isLinkageStatisticLoading,
        loadingError: linkageStatisticLoadingError,
        data: linkageStatistic,
        loadData: loadLinkageStatistic,
    } = useLinkageStatistic({accountId, licenseId, crmType: CrmTypes.MDCRM});

    const {
        busy: isSyncLinkageStatisticLoading,
        event: syncMsDynamicsLinkageStatistic,
    } = useSyncLinkageStatistic({accountId, crmType: CrmTypes.MDCRM});

    const {
        isLoading: isSettingsLoading,
        loadingError: settingsLoadingError,
        isChanged: isMsDynamicsSettingsChanged,

        data: msDynamicsSettings,
        setData: setMsDynamicsSettings,

        saveChanges: saveMsDynamicsSettings,
        discardChanges: discardMsDynamicsSettings,
        loadData: loadMsDynamicsSettings,
    } = useMsDynamicsSettings({accountId});

    const {
        isLoading: isCredentialsLoading,
        isConnectionStatusLoading,
        isUnlinkBusy,
        loadingError: credentialsLoadingError,
        isChanged: isMsDynamicsCredentialsChanged,

        data: msDynamicsCredentials,
        setData: setMsDynamicsCredentials,
        connectionStatus,

        saveChanges: saveMsDynamicsCredentials,
        unlinkConnection,
        testConnection,
        revokeConnection,
        discardChanges: discardMsDynamicsCredentials,
    } = useMSDynamicsCredentials({accountId, isCrmEnabled: enabled});

    const {
        isOAuthRedirectUrlLoading,
        oAuthRedirectUrlLoadingError,
        redirectToSalesforceOAuth,
    } = useGetMsDynamicsAuth({
        accountId,
        url: msDynamicsCredentials.crmUrl,
        onConnectionSuccessful: () => testConnection(msDynamicsCredentials),
    });

    const saveChanges = useCallback(async () => {
        const calls = [];

        if (isCrmLimitsChanged) {
            calls.push(await saveCrmLimits());
        }

        if (isMsDynamicsSettingsChanged) {
            calls.push(await saveMsDynamicsSettings());
        }

        if (isMsDynamicsCredentialsChanged) {
            calls.push(await saveMsDynamicsCredentials());
        }

        const errors = calls.filter(Boolean);

        if (errors.length) {
            onChangesSaved();
            setErrorMessage(errors.join(' '));
        } else {
            proceed();
        }
    }, [
        isMsDynamicsCredentialsChanged,
        isMsDynamicsSettingsChanged,
        isCrmLimitsChanged,
        saveCrmLimits,
        saveMsDynamicsSettings,
        saveMsDynamicsCredentials,
        onChangesSaved,
        setErrorMessage,
        proceed,
    ]);

    const connectByPassword = useCallback(async () => {
        const connectionError = await saveMsDynamicsCredentials();

        if (connectionError) {
            setErrorMessage(connectionError);
        }
    }, [saveMsDynamicsCredentials, setErrorMessage]);

    const discardChanges = useCallback(() => {
        discardCrmLimits();
        discardMsDynamicsSettings();
        discardMsDynamicsCredentials();

        onChangesDiscarded();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [
        discardCrmLimits,
        discardMsDynamicsSettings,
        discardMsDynamicsCredentials,
        onChangesDiscarded,
        setSuccessMessage,
    ]);

    const changeActiveCrm = useCallback(() => {
        if (isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(currentCrm !== CrmTypes.MDCRM ? CrmTypes.MDCRM : null);
            });
        } else {
            onChangeActiveCrm(currentCrm !== CrmTypes.MDCRM ? CrmTypes.MDCRM : null);
        }
    }, [isCurrentChunkChanged, discardChanges, currentCrm, onChangeActiveCrm, onShowModal]);

    const toggleCrmModules = useCallback<React.MouseEventHandler<HTMLDivElement>>(event => {
        if (enabled && isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(null);
                saveCrmModuleState(false);
            });
        } else if (enabled) {
            onChangeActiveCrm(null);
            saveCrmModuleState(false);
        } else {
            saveCrmModuleState(true);
        }
        event.preventDefault();
    }, [isCurrentChunkChanged, discardChanges, enabled, onChangeActiveCrm, saveCrmModuleState, onShowModal]);

    const syncLinkageStatistic = useCallback(async () => {
        const error = await syncMsDynamicsLinkageStatistic();

        if (error) {
            setErrorMessage(error);
        } else {
            setSuccessMessage('Reconciliation completed');
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }
        }
    }, [syncMsDynamicsLinkageStatistic, loadLinkageStatistic, setErrorMessage, setSuccessMessage]);

    useEffectWithDeps((prev, current) => {
        if (currentCrm === CrmTypes.MDCRM && prev.currentCrm !== current.currentCrm) {
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }

            if (loadMsDynamicsSettings) {
                loadMsDynamicsSettings();
            }
        }
    }, {
        currentCrm,
        loadMsDynamicsSettings,
        loadLinkageStatistic,
    });

    useEffect(() => {
        if (licenseId) {
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }
            if (loadMsDynamicsSettings) {
                loadMsDynamicsSettings();
            }
        }
    }, [licenseId, loadLinkageStatistic, loadMsDynamicsSettings]);

    const loadingError = [
        moduleLoadingError,
        limitsLoadingError,
        settingsLoadingError,
        credentialsLoadingError,
        oAuthRedirectUrlLoadingError,
    ].find(Boolean) || null;

    const isRowLoading = [
        isModuleLoading,
        isLimitsLoading,
    ].some(Boolean);

    const isLoading = [
        isSettingsLoading,
        isCredentialsLoading,
    ].some(Boolean);

    useEffect(() => {
        setIsChunkChanged([
            isCrmLimitsChanged,
            isMsDynamicsSettingsChanged,
            isMsDynamicsCredentialsChanged,
        ].some(Boolean));
    }, [
        setIsChunkChanged,
        isCrmLimitsChanged,
        isMsDynamicsSettingsChanged,
        isMsDynamicsCredentialsChanged,
    ]);

    useEffect(() => {
        if (runSaveChanges && isCurrentChunkChanged) {
            saveChanges();
        }
    }, [runSaveChanges, saveChanges, isCurrentChunkChanged]);

    useEffect(() => {
        if (runDiscardChanges && isCurrentChunkChanged) {
            discardChanges();
        }
    }, [runDiscardChanges, discardChanges, isCurrentChunkChanged]);

    const isActionDisabled = !isCurrentChunkChanged
        || isLoading
        || isRowLoading
        || !!loadingError
        || isConnectionStatusLoading;

    useEffect(() => {
        onActionDisabled(isActionDisabled);
    }, [isActionDisabled, onActionDisabled]);

    return (
        <CrmRow
            isLoading={isRowLoading}
            currentCrm={currentCrm}
            loadingError={loadingError}
            editDisabled={editDisabled}
            crmType={CrmTypes.MDCRM}
            crmName="MS Dynamics"
            enabled={enabled}
            onToggleCrmModules={toggleCrmModules}
            crmLimits={crmLimits}
            connectionStatus={(
                <ConnectionStatus {...{
                    enabled,
                    isTestReady: isMsDynamicTestReady(msDynamicsCredentials),
                    connectionStatus,
                    unlinkBusy: isUnlinkBusy,
                    onUnlink: unlinkConnection,
                }}
                />
            )}
            slideBoxButton={(
                <SlideBoxButton onClick={changeActiveCrm}>
                    {() => (
                        <Item className="pdy-xs">
                            {enabled && isMsDynamicTestReady(msDynamicsCredentials) ? (
                                <Group alignItems="center">
                                    <FlatButton
                                        startIcon={<Icon type={Icons.REPEAT}/>}
                                        busy={isConnectionStatusLoading}
                                        onClick={() => testConnection(msDynamicsCredentials)}>
                                        <b>Test connection</b>
                                    </FlatButton>
                                    <Item className="pr-xs">
                                        <BasicIconButton
                                            iconProps={{
                                                type: currentCrm === CrmTypes.MDCRM ? Icons.ANGLE_UP : Icons.ANGLE_DOWN,
                                                size: IconSizes.Size_12,
                                            }}
                                            disabled={
                                                isSomeChunkChanged(chunk => chunk.includes('crm')) &&
                                                currentCrm !== CrmTypes.MDCRM
                                            }
                                            onClick={changeActiveCrm}
                                        />
                                    </Item>
                                </Group>
                            ) : (
                                <FlatButton
                                    disabled={
                                        isSomeChunkChanged(chunk => chunk.includes('crm')) &&
                                        currentCrm !== CrmTypes.MDCRM
                                    }
                                    startIcon={<Icon type={Icons.PENCIL}/>}
                                    onClick={changeActiveCrm}
                                    endIcon={(
                                        <Icon
                                            type={currentCrm === CrmTypes.MDCRM ? Icons.ANGLE_UP : Icons.ANGLE_DOWN}
                                        />
                                    )}>
                                    Configure settings
                                </FlatButton>
                            )}
                        </Item>
                    )}
                </SlideBoxButton>
            )}>
            <MSDynamicsSection
                isLoading={isLoading}
                disabled={!enabled}
                readonly={editDisabled}
                isLinkageStatisticLoading={isLinkageStatisticLoading}
                linkageStatisticLoadingError={linkageStatisticLoadingError}
                isSyncLinkageLoading={isSyncLinkageStatisticLoading}
                isConnectionStatusLoading={isConnectionStatusLoading}
                isAuthRedirectUrlLoading={isOAuthRedirectUrlLoading}

                limits={crmLimits}
                credentials={msDynamicsCredentials}
                settings={msDynamicsSettings}
                linkageStatistic={linkageStatistic}
                orgId={connectionStatus?.status.crmUserInfo?.orgId}

                onChangeLimits={setCrmLimits}
                onChangeCredentials={setMsDynamicsCredentials}
                onChangeSettings={setMsDynamicsSettings}
                onSyncLinkageStatistic={syncLinkageStatistic}
                onSetupOAuthLogin={redirectToSalesforceOAuth}
                onSetupPasswordLogin={connectByPassword}
                onRevokeToken={revokeConnection}
            />
        </CrmRow>
    );
};
